
import { supabase } from '../lib/supabase';
import type {
  PharmacySale,
  PharmacySaleItem,
  Medicine,
  PatientSearchResult,
  CreatePatientData,
  CreateSaleRequest,
  CreateSaleResponse,
  IPDAdmission,
  HeldSale
} from '../types/pharmacy-pos';

export class PharmacyPOSService {
  async searchPatients(query: string): Promise<PatientSearchResult[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const searchTerm = query.trim().toLowerCase();

    const { data, error } = await supabase
      .from('patients')
      .select('id, unique_id, name, phone, email, gender, date_of_birth, address')
      .or(`unique_id.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)
      .limit(10);

    if (error) {
      console.error('Error searching patients:', error);
      throw new Error('Failed to search patients');
    }

    return (data || []).map(p => ({
      id: p.id,
      uhid: p.unique_id || '',
      name: p.name,
      phone: p.phone,
      email: p.email,
      gender: p.gender,
      age: p.date_of_birth ? this.calculateAge(p.date_of_birth) : undefined,
      address: p.address
    }));
  }

  async getPatientById(id: string): Promise<PatientSearchResult | null> {
    const { data, error } = await supabase
      .from('patients')
      .select('id, unique_id, name, phone, email, gender, date_of_birth, address')
      .eq('id', id)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      id: data.id,
      uhid: data.unique_id || '',
      name: data.name,
      phone: data.phone,
      email: data.email,
      gender: data.gender,
      age: data.date_of_birth ? this.calculateAge(data.date_of_birth) : undefined,
      address: data.address
    };
  }

  async getPatientByUHID(uhid: string): Promise<PatientSearchResult | null> {
    const { data, error } = await supabase
      .from('patients')
      .select('id, unique_id, name, phone, email, gender, date_of_birth, address')
      .eq('unique_id', uhid)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      id: data.id,
      uhid: data.unique_id || '',
      name: data.name,
      phone: data.phone,
      email: data.email,
      gender: data.gender,
      age: data.date_of_birth ? this.calculateAge(data.date_of_birth) : undefined,
      address: data.address
    };
  }

  async createPatient(patientData: CreatePatientData): Promise<PatientSearchResult> {
    const { data, error } = await supabase
      .from('patients')
      .insert({
        name: patientData.name,
        phone: patientData.phone,
        email: patientData.email,
        gender: patientData.gender,
        date_of_birth: patientData.date_of_birth,
        address: patientData.address
      })
      .select('id, unique_id, name, phone, email, gender, date_of_birth, address')
      .single();

    if (error || !data) {
      console.error('Error creating patient:', error);
      throw new Error('Failed to create patient');
    }

    return {
      id: data.id,
      uhid: data.unique_id || '',
      name: data.name,
      phone: data.phone,
      email: data.email,
      gender: data.gender,
      age: data.date_of_birth ? this.calculateAge(data.date_of_birth) : undefined,
      address: data.address
    };
  }

  async searchMedicines(query: string, page: number = 1, pageSize: number = 10): Promise<{ items: Medicine[]; total: number }> {
    let queryBuilder = supabase
      .from('products')
      .select('id, name, generic_name, category, price, created_at', { count: 'exact' });

    if (query && query.trim()) {
      const searchTerm = query.trim().toLowerCase();
      queryBuilder = queryBuilder.or(`name.ilike.%${searchTerm}%,generic_name.ilike.%${searchTerm}%`);
    }

    const offset = (page - 1) * pageSize;
    queryBuilder = queryBuilder
      .order('name', { ascending: true })
      .range(offset, offset + pageSize - 1);

    const { data, error, count } = await queryBuilder;

    if (error) {
      console.error('Error searching medicines:', error);
      throw new Error('Failed to search medicines');
    }

    const medicineIds = (data || []).map(m => m.id);
    const { data: batchData } = await supabase
      .from('product_batches')
      .select('product_id, batch_number, expiry_date, current_quantity, selling_price')
      .in('product_id', medicineIds)
      .gt('current_quantity', 0)
      .order('expiry_date', { ascending: true });

    const medicines: Medicine[] = (data || []).map(product => {
      const batches = (batchData || []).filter(b => b.product_id === product.id);
      const totalStock = batches.reduce((sum, b) => sum + (b.current_quantity || 0), 0);
      const nearestBatch = batches[0];

      return {
        id: product.id,
        name: product.name,
        generic_name: product.generic_name,
        category: product.category,
        mrp: nearestBatch?.selling_price || product.price || 0,
        stock: totalStock,
        expiry_date: nearestBatch?.expiry_date,
        batch_no: nearestBatch?.batch_number
      };
    });

    return {
      items: medicines,
      total: count || 0
    };
  }

  async getIPDAdmissions(patientId: string): Promise<IPDAdmission[]> {
    return [];
  }

  async createSale(saleData: CreateSaleRequest): Promise<CreateSaleResponse> {
    const subtotal = saleData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    
    let discount_amount = 0;
    if (saleData.discount_mode === 'percent') {
      discount_amount = subtotal * (saleData.discount_value / 100);
    } else {
      discount_amount = saleData.discount_value;
    }

    const vat_amount = (subtotal - discount_amount) * (saleData.vat_percent / 100);
    const grand_total = subtotal - discount_amount + vat_amount;
    const change_due = (saleData.amount_received || 0) - grand_total;

    const { data: sale, error: saleError } = await supabase
      .from('pharmacy_sales')
      .insert({
        sale_type: saleData.sale_type,
        patient_id: saleData.patient_id,
        patient_uhid: saleData.patient_uhid,
        patient_name: saleData.patient_name,
        patient_phone: saleData.patient_phone,
        patient_address: saleData.patient_address,
        ipd_encounter_id: saleData.ipd_encounter_id,
        admission_id: saleData.admission_id,
        bed_id: saleData.bed_id,
        doctor_id: saleData.doctor_id,
        visit_no: saleData.visit_no,
        prescription_no: saleData.prescription_no,
        subtotal,
        discount_mode: saleData.discount_mode,
        discount_value: saleData.discount_value,
        discount_amount,
        vat_percent: saleData.vat_percent,
        vat_amount,
        grand_total,
        payment_method: saleData.payment_method,
        amount_received: saleData.amount_received || 0,
        change_due,
        status: saleData.status,
        notes: saleData.notes
      })
      .select()
      .single();

    if (saleError || !sale) {
      console.error('Error creating sale:', saleError);
      throw new Error('Failed to create sale');
    }

    const items = saleData.items.map(item => ({
      sale_id: sale.id,
      medicine_id: item.medicine_id,
      medicine_name: item.medicine_name,
      generic_name: item.generic_name,
      batch_no: item.batch_no,
      expiry_date: item.expiry_date,
      quantity: item.quantity,
      unit_price: item.unit_price,
      mrp: item.mrp,
      line_total: item.quantity * item.unit_price
    }));

    const { error: itemsError } = await supabase
      .from('pharmacy_sale_items')
      .insert(items);

    if (itemsError) {
      console.error('Error creating sale items:', itemsError);
      throw new Error('Failed to create sale items');
    }

    if (saleData.status === 'paid') {
      await this.updateStock(saleData.items);
    }

    return {
      sale: sale as PharmacySale,
      invoice_no: sale.invoice_no
    };
  }

  async getHeldSales(): Promise<HeldSale[]> {
    const { data, error } = await supabase
      .from('pharmacy_sales')
      .select(`
        id,
        invoice_no,
        sale_type,
        patient_name,
        grand_total,
        created_at
      `)
      .eq('status', 'held')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching held sales:', error);
      return [];
    }

    const saleIds = (data || []).map(s => s.id);
    const { data: itemCounts } = await supabase
      .from('pharmacy_sale_items')
      .select('sale_id')
      .in('sale_id', saleIds);

    const countMap = (itemCounts || []).reduce((acc, item) => {
      acc[item.sale_id] = (acc[item.sale_id] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return (data || []).map(sale => ({
      id: sale.id,
      invoice_no: sale.invoice_no,
      sale_type: sale.sale_type,
      patient_name: sale.patient_name,
      grand_total: sale.grand_total,
      created_at: sale.created_at,
      items_count: countMap[sale.id] || 0
    }));
  }

  async getSaleById(id: string): Promise<PharmacySale | null> {
    const { data: sale, error } = await supabase
      .from('pharmacy_sales')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !sale) {
      return null;
    }

    const { data: items } = await supabase
      .from('pharmacy_sale_items')
      .select('*')
      .eq('sale_id', id);

    return {
      ...sale,
      items: items || []
    } as PharmacySale;
  }

  async updateSaleStatus(id: string, status: SaleStatus, paymentData?: { payment_method: PaymentMethod; amount_received: number }): Promise<void> {
    const updateData: any = { status };

    if (paymentData) {
      const sale = await this.getSaleById(id);
      if (sale) {
        updateData.payment_method = paymentData.payment_method;
        updateData.amount_received = paymentData.amount_received;
        updateData.change_due = paymentData.amount_received - sale.grand_total;
      }
    }

    const { error } = await supabase
      .from('pharmacy_sales')
      .update(updateData)
      .eq('id', id);

    if (error) {
      console.error('Error updating sale status:', error);
      throw new Error('Failed to update sale status');
    }

    if (status === 'paid') {
      const sale = await this.getSaleById(id);
      if (sale && sale.items) {
        await this.updateStock(sale.items.map(item => ({
          medicine_id: item.medicine_id,
          medicine_name: item.medicine_name,
          quantity: item.quantity,
          unit_price: item.unit_price
        })));
      }
    }
  }

  private async updateStock(items: Array<{ medicine_id: string; quantity: number }>): Promise<void> {
    for (const item of items) {
      const { data: batches } = await supabase
        .from('product_batches')
        .select('id, current_quantity')
        .eq('product_id', item.medicine_id)
        .gt('current_quantity', 0)
        .order('expiry_date', { ascending: true });

      if (!batches || batches.length === 0) {
        throw new Error(`Insufficient stock for medicine ${item.medicine_id}`);
      }

      let remainingQty = item.quantity;
      for (const batch of batches) {
        if (remainingQty <= 0) break;

        const deductQty = Math.min(remainingQty, batch.current_quantity);
        const newQty = batch.current_quantity - deductQty;

        await supabase
          .from('product_batches')
          .update({ current_quantity: newQty })
          .eq('id', batch.id);

        remainingQty -= deductQty;
      }

      if (remainingQty > 0) {
        throw new Error(`Insufficient stock for medicine ${item.medicine_id}`);
      }
    }
  }

  private calculateAge(dateOfBirth: string): number {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }
}

export const pharmacyPOSService = new PharmacyPOSService();
